import React, { useEffect, useRef, Suspense } from 'react';
import { arrayOf, shape, string, func, bool, object, oneOfType } from 'prop-types';
import { Switch, Route, withRouter, useLocation } from 'react-router-dom';
import { NotFoundPage } from './containers';

/** Scroll to top on navigation, or scroll to hash anchor if present */
const useScrollRestoration = () => {
  const location = useLocation();
  const prevLocation = useRef(null);

  useEffect(() => {
    if (prevLocation.current === location) return;
    prevLocation.current = location;

    if (!location.hash) {
      window.scroll({ top: 0, left: 0 });
    } else {
      const scrollToHash = () => {
        const el = document.querySelector(location.hash);
        if (el) {
          el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      };
      if (document.readyState === 'complete') {
        scrollToHash();
      } else {
        window.addEventListener('load', scrollToHash, { once: true });
      }
    }
  }, [location]);
};

/** Renders the matched route component with its props */
const RouteComponentRenderer = ({ route, match, location }) => {
  const { component: RouteComponent, extraProps } = route;
  useScrollRestoration();

  return (
    <RouteComponent
      params={match.params}
      location={location}
      {...extraProps}
    />
  );
};

// React.lazy returns an object (a thenable), not a plain function.
// We accept both so lazy-loaded and regular components both pass validation.
const componentType = oneOfType([func, object]).isRequired;

RouteComponentRenderer.propTypes = {
  route: shape({ component: componentType }).isRequired,
  match: shape({
    params: object.isRequired,
    url: string.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
};

const routeShape = shape({
  name: string.isRequired,
  path: string.isRequired,
  exact: bool,
  component: componentType,
});

const Routes = ({ routes }) => {
  const toRouteComponent = r => {
    const isExact = r.exact != null ? r.exact : true;
    return (
      <Route
        key={r.name}
        path={r.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentRenderer
            route={r}
            match={matchProps.match}
            location={matchProps.location}
          />
        )}
      />
    );
  };

  return (
    <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
      <Switch>
        {routes.map(toRouteComponent)}
        <Route component={NotFoundPage} />
      </Switch>
    </Suspense>
  );
};

Routes.propTypes = {
  routes: arrayOf(routeShape).isRequired,
};

export default withRouter(Routes);
